/** Display order for workorder_type sections in the detail modal. */
export const WORKORDER_TYPE_DISPLAY_ORDER = [
  'STANDARD ANALYSIS',
  'STANDARD + SEQUENTIAL',
  'SEQUENTIAL LEACH',
  'RECEPTION WO',
];

const NON_QAQC_TYPES = new Set(['SEQUENTIAL LEACH', 'RECEPTION WO']);

/**
 * Normalize type strings from mv_drilling_progress_metrics (comma-separated lists).
 */
export function normalizeWorkorderType(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).replace(/\s+/g, ' ').trim().toUpperCase();
  return s || null;
}

/** Whether to show QAQC pass/fail for this workorder type. */
export function isQaqcRelevantWorkorderType(type) {
  const normalized = normalizeWorkorderType(type);
  if (normalized == null) return true;
  return !NON_QAQC_TYPES.has(normalized);
}

/**
 * Build Map workorder_id -> normalized type from metrics row (aligned lists).
 */
export function buildTypeByWorkorder(sampleTrackingInfo, parseOrderedList) {
  const map = new Map();
  if (!sampleTrackingInfo || typeof parseOrderedList !== 'function') {
    return map;
  }
  const woList = parseOrderedList(sampleTrackingInfo.workorder_numbers);
  const typeList = parseOrderedList(
    sampleTrackingInfo.workorder_types || sampleTrackingInfo.workorder_type || ''
  );
  woList.forEach((woNum, i) => {
    if (!woNum || map.has(woNum)) return;
    const t = normalizeWorkorderType(typeList[i]);
    if (t) map.set(woNum, t);
  });
  return map;
}

function typeSortRank(type) {
  const normalized = normalizeWorkorderType(type);
  if (normalized == null) return WORKORDER_TYPE_DISPLAY_ORDER.length + 1;
  const idx = WORKORDER_TYPE_DISPLAY_ORDER.indexOf(normalized);
  return idx >= 0 ? idx : WORKORDER_TYPE_DISPLAY_ORDER.length;
}

/** Stable sort workorder detail objects by type group order. */
export function sortWorkorderDetailsByType(details) {
  if (!details?.length) return details || [];
  return details
    .map((wo, index) => ({ wo, index }))
    .sort((a, b) => {
      const ra = typeSortRank(a.wo.workorder_type);
      const rb = typeSortRank(b.wo.workorder_type);
      if (ra !== rb) return ra - rb;
      return a.index - b.index;
    })
    .map(({ wo }) => wo);
}

/**
 * Group sorted details for modal rendering: ordered type keys + Other bucket.
 */
export function groupWorkorderDetailsByType(details) {
  const groups = [];
  const byType = new Map();

  (details || []).forEach((wo) => {
    const normalized = normalizeWorkorderType(wo.workorder_type);
    const inKnown =
      normalized != null && WORKORDER_TYPE_DISPLAY_ORDER.includes(normalized);
    const key = inKnown ? normalized : '__other__';
    if (!byType.has(key)) byType.set(key, []);
    byType.get(key).push(wo);
  });

  WORKORDER_TYPE_DISPLAY_ORDER.forEach((typeLabel) => {
    const items = byType.get(typeLabel);
    if (items?.length) {
      groups.push({ typeLabel, workorders: items });
    }
  });

  const other = byType.get('__other__');
  if (other?.length) {
    groups.push({ typeLabel: 'Other', workorders: other });
  }

  return groups;
}

/** True if any workorder on the hole has a known type from metrics. */
export function holeHasWorkorderTypes(workorderDetails) {
  return (workorderDetails || []).some(
    (wo) => normalizeWorkorderType(wo.workorder_type) != null
  );
}
